"use client";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { fetchSharedStats } from "../../api/api";
import NewAdmissions from "../../mycomp/Dashboard/School/NewAdmissions";
import Courses from "../../mycomp/Dashboard/LMS/PersonalWebUsage";

const ViewStatistics = () => {
    const location = useLocation();
    const token = location.pathname.split("/").pop();
    const user_id = location.state?.user_id;

    // ✅ user state
    const [user, setUser] = useState({
        name: "Unknown User",
        email: "No Email",
        workspace_name: "No Workspace"
    });

    const [totalWorked, setTotalWorked] = useState("00h 00m");
    const [totalActive, setTotalActive] = useState("00h 00m");
    const [productivity, setProductivity] = useState(0);
    const [idlePercent, setIdlePercent] = useState(0);
    const [clockIn, setClockIn] = useState("-");
    const [clockOut, setClockOut] = useState("-");
    const [summary, setSummary] = useState([]);

    // helpers
    const formatDuration = (seconds) => {
        const sec = Number(seconds) || 0;
        const h = Math.floor(sec / 3600);
        const m = Math.floor((sec % 3600) / 60);
        return `${h}h ${m}m`;
    };

    const formatClock = (time) => {
        if (!time) return "-";
        return new Date(time).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatDate = (date) => {
        if (!date) return "-";
        return new Date(date).toLocaleDateString();
    };

    useEffect(() => {
        if (!token) return;

        const loadStats = async () => {
            try {
                const data = await fetchSharedStats(token);

                // ✅ totals
                const worked = Number(data.stats.total_worked_seconds) || 0;
                const active = Number(data.stats.total_active_seconds) || 0;

                setTotalWorked(formatDuration(worked));
                setTotalActive(formatDuration(active));

                setProductivity(data.stats.productive_percent || 0);
                setIdlePercent(data.stats.idle_percent || 0);

                setClockIn(formatClock(data.stats.today_clock_in));
                setClockOut(formatClock(data.stats.today_clock_out));

                // ✅ weekly summary (FIXED)
                const weekly = Array.isArray(data.weekly) ? data.weekly : [];

                const capped = weekly.map(row => ({
                    ...row,
                    productivity: row.productivity > 100 ? 100 : row.productivity,
                }));

                setSummary(capped);

                // ✅ user info
                setUser({
                    name: data.member?.name || "Unknown User",
                    email: data.member?.email || "No Email",
                    workspace_name: data.member?.workspace_name || "No Workspace"
                });

            } catch (err) {
                console.error("Failed to fetch stats:", err);
            }
        };

        loadStats();
    }, [token]);

    return (
        <>
            {/* USER HEADER */}
            <Row className="mb-3">
                <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12">
                    <Card className="bg-primary border-0 rounded-3 welcome-box py-5 px-4">
                        <div className="d-flex align-items-center">
                            <div
                                className="d-flex justify-content-center align-items-center me-3"
                                style={{
                                    width: "60px",
                                    height: "60px",
                                    borderRadius: "50%",
                                    backgroundColor: "#ffffff30",
                                    color: "#fff",
                                    fontSize: "28px",
                                    fontWeight: "600",
                                }}
                            >
                                {user.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <h2 className="mb-1 text-white">{user.name}</h2>
                                <p className="mb-0 text-light">{user.email}</p>
                                <p className="mb-0 text-light" style={{ fontSize: "14px", opacity: 0.8 }}>
                                    {user.workspace_name}
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-3 col-sm-3">
                    <div className="card bg-white bg-info bg-opacity-10 p-2 rounded-3">
                        <p>Total Worked Hours</p>
                        <h2>{totalWorked}</h2>
                        <p>Clocked-in hours (excludes breaks & screen lock)</p>
                    </div>
                </div>

                <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-3 col-sm-3">
                    <div className="card bg-white bg-opacity-10 p-2 rounded-3">
                        <p>Active Time</p>
                        <h2>{totalActive}</h2>
                        <p>Time spent actively engaging with the system</p>
                    </div>
                </div>
            </Row>

            {/* METRICS */}
            <Row className="mb-3">
                <div className="col-md-3 mt-3">
                    <div className="card bg-white bg-info bg-opacity-10 p-2 rounded-3">
                        <p>Productive Time Spent %</p>
                        <h2>{productivity}%</h2>
                    </div>
                </div>

                <div className="col-md-3 mt-3">
                    <div className="card bg-white bg-info bg-opacity-10 p-2 rounded-3">
                        <p>Idle Time Spent %</p>
                        <h2>{idlePercent}%</h2>
                    </div>
                </div>

                <div className="col-md-3 mt-3">
                    <div className="card bg-white bg-info bg-opacity-10 p-2 rounded-3">
                        <p>Clock-in Time</p>
                        <h2>{clockIn}</h2>
                    </div>
                </div>

                <div className="col-md-3 mt-3">
                    <div className="card bg-white bg-info bg-opacity-10 p-2 rounded-3">
                        <p>Clock-out Time</p>
                        <h2>{clockOut}</h2>
                    </div>
                </div>
            </Row>

            {/* PLACEHOLDERS */}
            <Row className="mb-3">
                <Col sm={12} lg={8}>
                    <Courses user_id={user_id} />
                </Col>
                <Col sm={12} lg={4}>
                    <NewAdmissions user_id={user_id} />
                </Col>
            </Row>

            {/* WEEKLY SUMMARY */}
            <Row className="mb-3">
                <div className="col-12 mt-3 mb-3">
                    <div className="card bg-white bg-info bg-opacity-10 p-4 rounded-3">
                        <h3 className="mb-4">Clock In & Out Summary (Last 7 Days)</h3>
                        <div className="table-responsive">
                            <table className="table align-middle table-bordered">
                                <thead className="table-light">
                                    <tr>
                                        <th>Date</th>
                                        <th>Clock In</th>
                                        <th>Clock Out</th>
                                        <th>Total Worked</th>
                                        <th>Active Time</th>
                                        <th>Idle Time</th>
                                        <th>Productivity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summary.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center">
                                                No records found
                                            </td>
                                        </tr>
                                    ) : (
                                        summary.map((row, index) => (
                                            <tr key={index}>
                                                <td>{formatDate(row.date)}</td>
                                                <td>{formatClock(row.clock_in)}</td>
                                                <td>{formatClock(row.clock_out)}</td>
                                                <td>{formatDuration(row.worked_seconds)}</td>
                                                <td>{formatDuration(row.active_seconds)}</td>
                                                <td>{formatDuration(row.idle_seconds)}</td>
                                                <td>{row.productivity}%</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </Row>
        </>
    );
};

export default ViewStatistics;